import { skipToken, useQuery } from "@tanstack/react-query";
import {
  type FollowRequestCountDto,
  type FollowRequestDirection,
  type FollowRequestDto,
  type FollowRequestSummaryDto,
  getSocialFollowRequests,
  getSocialFollowRequestsCount,
  getSocialFollowRequestsSummary,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";

export const followRequestsKeys = {
  all: ["SOCIAL_FOLLOW_REQUESTS"] as const,
  byDirection: (direction: FollowRequestDirection) =>
    [...followRequestsKeys.all, direction] as const,
  count: () => [...followRequestsKeys.all, "COUNT"] as const,
  summary: () => [...followRequestsKeys.all, "SUMMARY"] as const,
};

/**
 * How many follow requests are waiting for the caller — the menu badge. One COUNT on the server
 * over the same population the incoming list shows, fetched on every app load, so it must not be
 * the list itself. `isEnabled` lets hosts skip it for personas outside the social graph.
 */
export const useFollowRequestCountQuery = (isEnabled = true) =>
  useQuery<FollowRequestCountDto>({
    queryKey: followRequestsKeys.count(),
    queryFn: processResponseWithApiProblemDetails(() =>
      getSocialFollowRequestsCount(),
    ),
    enabled: isEnabled,
  });

/**
 * The caller's pending follow requests (the badge reads useFollowRequestCountQuery instead).
 * `isEnabled` lets hosts skip the fetch for personas outside the social graph
 * (back-office roles have no social surface).
 */
export const useFollowRequestsQuery = (
  direction: FollowRequestDirection,
  isEnabled = true,
) =>
  useQuery<Array<FollowRequestDto>>({
    queryKey: followRequestsKeys.byDirection(direction),
    queryFn: processResponseWithApiProblemDetails(() =>
      getSocialFollowRequests(direction),
    ),
    enabled: isEnabled,
  });

/**
 * What going public would do to the pending follow requests (all get approved) — feeds the
 * visibility-switch confirm. Fetched only while that confirm can be shown.
 */
export const useFollowRequestSummaryQuery = (isEnabled: boolean) =>
  useQuery<FollowRequestSummaryDto>({
    queryKey: followRequestsKeys.summary(),
    queryFn: isEnabled
      ? processResponseWithApiProblemDetails(() =>
          getSocialFollowRequestsSummary(),
        )
      : skipToken,
  });
